import { useCallback, useMemo, useRef } from 'react';
import { useShowToast } from '@/hooks/useShowToast';
import { useFileValidation } from '@/hooks/useFileValidation';
import {
  ATTACHMENT_ACCEPT,
  ATTACHMENT_TOOLTIP,
  KULA_PER_FILE_PASSTHROUGH_MB,
  MAX_ATTACHMENT_FILES,
  TOTAL_SIZE_TOAST,
  exceedsTotalAttachmentSize,
} from './attachmentLimits';

/**
 * kula `Editor` attachment props for an email surface.
 *
 * kula's own caps (1 MB per file, 5 files) reject silently, so they are raised
 * out of the way and `onAttachFiles` runs the shared type check and the 15 MB
 * total cap instead — both of which toast. Returned together so a surface can't
 * raise the caps without the guard (ENG-4061).
 */
export const useAttachmentGuard = (
  existing: Array<{ size?: number }>,
  onAccept: (files: File[]) => void
) => {
  const toast = useShowToast();
  const { handleAttachmentValidation } = useFileValidation();
  // Both hooks return fresh identities each render; a new handler remounts kula's editor.
  const latest = useRef({ toast, validate: handleAttachmentValidation });
  latest.current = { toast, validate: handleAttachmentValidation };

  const onAttachFiles = useCallback(
    async (files: File[]) => {
      if (!files.length) return;
      // Type before size; the validator raises its own toast.
      if (!(await latest.current.validate({ files }))) return;
      if (exceedsTotalAttachmentSize(existing, files)) {
        latest.current.toast(TOTAL_SIZE_TOAST);
        return;
      }
      onAccept(files);
    },
    [existing, onAccept]
  );

  return useMemo(
    () => ({
      attachmentMaxFiles: MAX_ATTACHMENT_FILES,
      attachmentMaxFileSizeMb: KULA_PER_FILE_PASSTHROUGH_MB,
      attachmentAccept: ATTACHMENT_ACCEPT,
      attachmentTooltip: ATTACHMENT_TOOLTIP,
      onAttachFiles,
    }),
    [onAttachFiles]
  );
};
